//jsxhook
import {
  TextVariants,
  H6,
  GraphicArrow,
  Label2,
  ButtonVariants,
  FontWeight,
  LineHeights,
  Fonts,
  radii,
  StandardXAxisSpace,
  zIndices
} from "@sixty-six-north/ui-system"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Grid, Flex, Box } from "theme-ui"
import { useInterval } from "../../Hooks"
import { PrismicPrimaryAdapter, PrismicItemAdapter } from "../PrismicAdapters"
import { PrismicSlice } from "../PrismicModels"
import { calculateTimeUntil, TimeRemaining } from "./Countdown"
import { PrismicElementWrapper } from "./Layout"

export const CompetitionHeader: React.FC<{ data: PrismicSlice }> = ({
  data
}) => {
  const { t } = useTranslation()
  const adapter = new PrismicPrimaryAdapter(data.primary)
  const benefits = data.primary.benefits_list?.map(it => it.text) || []

  const calculateTimeRemaining = () =>
    calculateTimeUntil(adapter.competitionEndDate)
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    calculateTimeRemaining()
  )

  useInterval(() => setTimeRemaining(calculateTimeRemaining()), 1000)

  return (
    <PrismicElementWrapper prismicStyle={data.primary}>
      <Flex
        sx={{
          flex: 1,
          flexDirection: ["column", "column", "column", "row"]
        }}
      >
        <Flex
          sx={{
            flexDirection: "column",
            flex: 1,
            pr: [0, 0, 0, 64]
          }}
        >
          <Box
            sx={{
              mb: [32, 40, 64]
            }}
          >
            <h1
              sx={{
                color: "grey.1",
                fontFamily: Fonts.text,
                fontWeight: FontWeight.textRegular,
                lineHeight: LineHeights.heading,
                fontSize: [
                  "scale2",
                  "scale2",
                  "scale3",
                  "scale3",
                  "scale4",
                  "scale5"
                ],
                mb: 8
              }}
            >
              {adapter.section_title()}
            </h1>
            <p
              sx={{
                color: "black.1",
                fontFamily: Fonts.text,
                fontWeight: FontWeight.textRegular,
                lineHeight: 1.3,
                fontSize: [
                  "scale2",
                  "scale2",
                  "scale3",
                  "scale3",
                  "scale4",
                  "scale5"
                ],
                maxWidth: "31ch"
              }}
            >
              {adapter.the_text()}
            </p>
          </Box>

          <Box
            sx={{
              maxWidth: [null, null, "50%"]
            }}
          >
            <p
              sx={{
                variant: `text.${TextVariants.technicalParagraphSmall}`,
                color: "grey.0",
                mb: 32
              }}
            >
              {data.primary.date_label}
            </p>
            <Grid columns={[4, "1fr 1fr 1fr 1fr"]}>
              <Label2 sx={{ color: "grey.1" }}>{t("countdown:days")}</Label2>
              <Label2 sx={{ color: "grey.1" }}>{t("countdown:hours")}</Label2>
              <Label2 sx={{ color: "grey.1" }}>{t("countdown:minutes")}</Label2>
              <Label2 sx={{ color: "grey.1" }}>{t("countdown:seconds")}</Label2>
              <H6 sx={{ color: "black.1" }} suppressHydrationWarning>
                {timeRemaining.days}
              </H6>
              <H6 sx={{ color: "black.1" }} suppressHydrationWarning>
                {timeRemaining.hours}
              </H6>
              <H6 sx={{ color: "black.1" }} suppressHydrationWarning>
                {timeRemaining.minutes}
              </H6>
              <H6 sx={{ color: "black.1" }} suppressHydrationWarning>
                {timeRemaining.seconds}
              </H6>
            </Grid>
          </Box>

          <Flex
            sx={{
              mt: 32,
              "& a": {
                width: ["100%", null, null, "auto"],

                variant: `buttons.${ButtonVariants.primary}`
              },
              "& a + a": {
                variant: `buttons.${ButtonVariants.secondary}`,
                ml: 12
              }
            }}
          >
            {data.items
              .map(it => new PrismicItemAdapter(it))
              .map((it, idx) => (
                <a key={`btn-${idx}`} href={`#${it.anchorId()}`}>
                  {it.label()}
                </a>
              ))}
          </Flex>
        </Flex>

        <Flex
          sx={{
            width: ["100%", "100%", "100%", "33%"],
            mt: [48, 48, 48, 0],
            position: "relative",
            alignItems: ["flex-start", null, null, "center"]
          }}
        >
          <Box
            aria-hidden={true}
            sx={{
              position: "absolute",
              height: 56,
              width: 56,
              right: [24, 24, 0, 0, 0, 0],
              mt: [-32, null, null, 0],
              mr: [12, null, null, -48],
              zIndex: zIndices.page,
              transform: ["rotate(-90deg)", null, null, "none"]
            }}
          >
            <GraphicArrow />
          </Box>

          <Flex
            sx={{
              flex: 1,
              "& :after": {
                content: '" "',
                width: [0, 0, 0, 48, 48, StandardXAxisSpace],
                height: "100%",
                bg: "grey.4",
                position: "absolute",
                top: 0,
                left: "100%",
                bottom: 0
              }
            }}
          >
            <ul
              sx={{
                bg: "grey.4",
                borderRadius: radii.clickable,
                flex: 1,
                my: "auto",
                variant: `text.${TextVariants.technicalParagraphSmall}`,
                listStyle: "none",
                py: [32, 32, 32, 40],
                pl: [24, 24, 24, 40],
                pr: [24, 24, 24, 0],
                position: "relative",
                "& li": {
                  maxWidth: "34ch"
                },
                "& li + li": {
                  mt: 12
                }
              }}
            >
              {benefits.map((it, idx) => (
                <li key={`benefit-${idx}`}>{it}</li>
              ))}
            </ul>
          </Flex>
        </Flex>
      </Flex>
    </PrismicElementWrapper>
  )
}
